import React from 'react';
import { Dropdown, Popconfirm, Button } from 'antd';
import { TeamOutlined, MoreOutlined, LockOutlined, PlusOutlined } from '@ant-design/icons';
import { Text, Pill } from '@clockout-dev/design-elements';
import SectionCard from '../../common/sectionCard/SectionCard';
import styles from '../styles/roles.module.scss';

/**
 * The FI's role list — selecting a row loads it into the scope-assignment
 * panel. Only the owner role and the current default role are protected
 * from deletion (is_owner_role blocks it outright; is_default_role requires
 * "Set as default" on another role first).
 *
 * `canManage` gates every write affordance (New role, Set as default,
 * Delete role) — someone with only screen:roles:view can browse this list
 * read-only but never mutate it. Row selection itself stays available so a
 * view-only holder can still inspect what each role can do.
 */
function RoleListPanel({ roles, selectedRoleId, onSelectRole, onNewRole, onRenameRole, onEditRole, onDeleteRole, onSetDefaultRole, canManage }) {
    return (
        <SectionCard icon={<TeamOutlined />} title="Roles at this FI">
            <div className={styles.roleList}>
                {roles.map((role) => {
                    const isSelected = role.id === selectedRoleId;
                    const scopeCount = role.scope_keys?.length || 0;
                    const stopMenuClose = ({ domEvent }) => domEvent.stopPropagation();

                    const menuItems = !canManage ? [] : [
                        {
                            key: 'edit',
                            label: 'Edit role',
                            onClick: ({ domEvent }) => {
                                domEvent.stopPropagation();
                                onEditRole?.(role);
                            },
                        },
                        ...(!role.is_default_role ? [{
                            key: 'set-default',
                            label: (
                                <Popconfirm
                                    title="Make this the default role?"
                                    description="New members added without an explicit role will get this one."
                                    okText="Set default"
                                    cancelText="Cancel"
                                    placement="topRight"
                                    onConfirm={() => onSetDefaultRole(role.id)}
                                >
                                    <span onClick={(e) => e.stopPropagation()}>Set as default</span>
                                </Popconfirm>
                            ),
                            onClick: stopMenuClose,
                        }] : []),
                        ...(!role.is_owner_role && !role.is_default_role ? [{
                            key: 'delete',
                            danger: true,
                            label: (
                                <Popconfirm
                                    title="Delete this role?"
                                    description="Anyone currently holding it will lose the scopes it granted."
                                    okText="Delete"
                                    cancelText="Cancel"
                                    okButtonProps={{ danger: true }}
                                    placement="topRight"
                                    onConfirm={() => onDeleteRole(role.id)}
                                >
                                    <span onClick={(e) => e.stopPropagation()}>Delete role</span>
                                </Popconfirm>
                            ),
                            onClick: stopMenuClose,
                        }] : []),
                    ];

                    return (
                        <div
                            key={role.id}
                            className={`${styles.roleRow} ${isSelected ? styles.roleRowSelected : ''}`}
                            onClick={() => onSelectRole(role.id)}
                        >
                            <div className={styles.roleRowMain}>
                                <Text as="span" size="s" className={styles.roleName}>{role.name}</Text>
                                {role.is_owner_role ? (
                                    <LockOutlined title="Owner role — cannot be deleted" className={styles.roleOwnerIcon} />
                                ) : null}
                                {role.is_default_role ? (
                                    <Pill label="DEFAULT" />
                                ) : null}
                            </div>
                            <div className={styles.roleRowMeta}>
                                <Text as="span" size="xs" className={styles.roleScopeCount}>
                                    {scopeCount} scope{scopeCount === 1 ? '' : 's'}
                                </Text>
                                {menuItems.length > 0 ? (
                                    <Dropdown menu={{ items: menuItems }} trigger={['click']} placement="bottomRight">
                                        <button
                                            type="button"
                                            className={styles.roleRowActions}
                                            onClick={(e) => e.stopPropagation()}
                                            aria-label={`Actions for ${role.name}`}
                                        >
                                            <MoreOutlined />
                                        </button>
                                    </Dropdown>
                                ) : null}
                            </div>
                        </div>
                    );
                })}
                {canManage ? (
                    <Button
                        type="dashed"
                        icon={<PlusOutlined />}
                        onClick={onNewRole}
                        className={styles.newRoleButton}
                        block
                    >
                        New role
                    </Button>
                ) : null}
            </div>
        </SectionCard>
    );
}

export default RoleListPanel;
